// The Outlook handoff, shared by every outreach surface.
//
// Extracted from DailySummary.js on 2026-09-14 when VIP Win-back became a second caller. These
// are pure functions with no dependencies, and this repo already carries one documented
// mirror-drift bug from copying a helper instead of sharing it.

// UTF-8-safe base64. Email bodies carry curly quotes and accented names, and btoa() alone
// throws on anything outside latin1.
export function utf8ToBase64(str) {
  const bytes = new TextEncoder().encode(str);
  let bin = '';
  bytes.forEach((b) => { bin += String.fromCharCode(b); });
  return btoa(bin);
}

// ⚠️ `X-Unsent: 1` IS THE WHOLE TRICK. It makes classic Outlook open the file as a NEW,
// ready-to-send message composed from the rep's own account, rather than as a received item
// sitting in a folder. Without it the rep has to copy the body into a fresh mail by hand.
export function buildEml({ fromEmail, fromName, to, subject, html }) {
  const b64 = utf8ToBase64(html).replace(/(.{76})/g, '$1\r\n');
  const from = fromName ? `${fromName} <${fromEmail}>` : fromEmail;
  return [
    `From: ${from}`,
    `To: ${to}`,
    `Subject: ${subject}`,
    `Date: ${new Date().toUTCString()}`,
    `X-Unsent: 1`,
    `MIME-Version: 1.0`,
    `Content-Type: text/html; charset=utf-8`,
    `Content-Transfer-Encoding: base64`,
    '',
    b64,
  ].join('\r\n');
}

export function downloadEml(filename, content) {
  const blob = new Blob([content], { type: 'message/rfc822' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export const slugName = (s) =>
  String(s || 'customer').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'customer';

// ── Open tracking ─────────────────────────────────────────────────────────────────────────────
// Rachael, 2026-09-16: replies to the invites fell from ~9% to ~2-3% in three weeks and there was
// no way to tell "landing in Junk" from "read and ignored". An open pixel cannot prove delivery,
// but a sudden fall in opens at one mailbox provider is the clearest junk signal we can get.
//
// ⚠️ AN OPEN IS NOISY IN BOTH DIRECTIONS. Apple Mail pre-fetches every image (over-counts),
// Outlook blocks them by default (under-counts), Gmail proxies and caches. Read these as a TREND
// per provider, never as a rate to quote. See references/vip/reference_vip_outreach_offer_tracking.md.

export const TRACK_HOST = 'https://vip.crabplace.com';

// 16 random bytes, base64url, 22 characters - the same strength as the server-side backfill.
// Chosen HERE because the email is built before the send is logged, so the pixel URL needs a
// token the database has not seen yet. The unique index refuses a collision.
export function newSendToken() {
  const b = new Uint8Array(16);
  window.crypto.getRandomValues(b);
  let s = '';
  b.forEach((x) => { s += String.fromCharCode(x); });
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// ⚠️ THE PIXEL IS ONLY EMBEDDED ONCE ITS HOST ACTUALLY ANSWERS. An image pointing at a domain
// that does not resolve shows as a broken-picture box in some clients, which is the one thing a
// personal note from a rep cannot carry. So the composer asks first, once per page load, and
// sends without a pixel until vip.crabplace.com is live - which means the day DNS lands, this
// switches itself on with no deploy. The probe token is one no send ever carries, so the pixel
// records nothing for it.
let probe = null;
export function trackingReady() {
  if (probe) return probe;
  probe = new Promise((resolve) => {
    const img = new Image();
    const done = (ok) => { clearTimeout(timer); resolve(ok); };
    const timer = setTimeout(() => done(false), 4000);
    img.onload = () => done(img.width === 1);
    img.onerror = () => done(false);
    img.src = TRACK_HOST + '/p/availabilityprobe.gif?ts=' + Date.now();
  });
  // A failed probe is retried on the next send rather than remembered for the whole session,
  // so a rep who had the page open before DNS landed still starts tracking without a reload.
  probe.then((ok) => { if (!ok) probe = null; });
  return probe;
}

// Appended to the html at build time, never put into the editable body, so a rep tidying the
// draft cannot delete it without noticing. 1x1, no alt text, no border.
export function pixelTag(token) {
  return `<img src="${TRACK_HOST}/p/${token}.gif" width="1" height="1" alt="" `
    + 'style="display:block;width:1px;height:1px;border:0;margin:0;padding:0;" />';
}
